import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import EcoConfirmDialog from "../../../components/eco-ui/EcoConfirmDialog";
import type { RootState } from "../../../store/store";
import {
  DpEmployeeWithPendencies,
  getPendenciesSummaryApi,
  listPendenciesApi,
  PENDENCY_CATEGORY_LABELS,
  resolvePendencyApi,
} from "../api/pendenciesApi";
import { formatCpf } from "../utils/format";
import { DP_EMPLOYEE_PAGE } from "../routes";
import "../pages/Dp.css";

const PAGE_SIZE = 10;

const CATEGORY_BADGE: Record<string, string> = {
  DOCUMENTOS: "dpBadge--red",
  CONTRATO: "dpBadge--orange",
  ADICIONAIS: "dpBadge--blue",
  DEPENDENTES: "dpBadge--gray",
  FICHA: "dpBadge--gray",
};

/**
 * Aviso "Cadastros com pendência" no topo de todas as telas do DP (montado pelo
 * `DpRoute`). Clicar abre um modal paginado com os colaboradores que têm
 * pendência aberta; cada pendência pode ser marcada como resolvida ali mesmo.
 * Some sozinho quando não há pendência.
 */
const PendingRegistrationsNotice: React.FC = () => {
  const token = useSelector((state: RootState) => state.auth.accessToken) as string;
  const location = useLocation();
  const navigate = useNavigate();

  const [count, setCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [items, setItems] = useState<DpEmployeeWithPendencies[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [resolving, setResolving] = useState<string | null>(null);
  /** Pendência aguardando confirmação do "Resolver" — nada é resolvido sem ela. */
  const [confirming, setConfirming] = useState<{
    employeeName: string;
    pendency: DpEmployeeWithPendencies["pendencies"][number];
  } | null>(null);

  const loadSummary = useCallback(async () => {
    if (!token) return;
    try {
      const summary = await getPendenciesSummaryApi(token);
      setCount(summary.employees);
    } catch {
      // Aviso é acessório: se a consulta falhar, a tela segue sem ele.
    }
  }, [token]);

  // Recarrega a cada navegação dentro do DP: uma edição na ficha pode ter mudado o quadro.
  useEffect(() => {
    loadSummary();
  }, [loadSummary, location.pathname]);

  const loadPage = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listPendenciesApi(token, { page, limit: PAGE_SIZE, q: query || undefined });
      setItems(data.items);
      setTotal(data.total);
    } catch (error: any) {
      toast.error(error?.message || "Erro ao listar as pendências de cadastro.");
    } finally {
      setLoading(false);
    }
  }, [token, page, query]);

  useEffect(() => {
    if (open) loadPage();
  }, [open, loadPage]);

  const handleResolve = async () => {
    if (!confirming) return;
    const pendencyId = confirming.pendency.id;
    setResolving(pendencyId);
    try {
      await resolvePendencyApi(token, pendencyId);
      toast.success("Pendência marcada como resolvida.");
      setConfirming(null);
      await Promise.all([loadPage(), loadSummary()]);
    } catch (error: any) {
      toast.error(error?.message || "Erro ao resolver a pendência.");
    } finally {
      setResolving(null);
    }
  };

  const handleOpenEmployee = (id: string) => {
    setOpen(false);
    navigate(DP_EMPLOYEE_PAGE(id));
  };

  const handleSearch = (event: React.FormEvent) => {
    event.preventDefault();
    setPage(1);
    setQuery(search.trim());
  };

  if (count === 0 && !open) return null;

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <>
      {count > 0 && (
        <button type="button" className="dpPendingNotice" onClick={() => setOpen(true)}>
          <span className="dpPendingNotice__icon" aria-hidden="true">!</span>
          Cadastros com pendência
          <span className="dpPendingNotice__count">{count}</span>
        </button>
      )}

      {open && (
        <div className="dpModalOverlay" onClick={() => setOpen(false)}>
          <div className="dpModal dpModal--wide" onClick={(e) => e.stopPropagation()}>
            <header className="dpModal__header">
              <h2 className="dpModal__title">Cadastros com pendência</h2>
              <button className="dpModal__close" onClick={() => setOpen(false)} aria-label="Fechar">
                ✕
              </button>
            </header>

            <div className="dpModal__body">
              <form className="dpPendingSearch" onSubmit={handleSearch}>
                <input
                  className="dpInput"
                  placeholder="Buscar por nome"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                <button type="submit" className="dpBtn dpBtn--secondary">
                  Buscar
                </button>
              </form>

              {loading && items.length === 0 ? (
                <p className="dpEmpty">Carregando...</p>
              ) : items.length === 0 ? (
                <p className="dpEmpty">Nenhum colaborador com pendência.</p>
              ) : (
                <ul className="dpPendingList">
                  {items.map((employee) => (
                    <li key={employee.id} className="dpPendingItem">
                      <div className="dpPendingItem__head">
                        <div>
                          <strong>{employee.fullName}</strong>
                          <span className="dpPendingItem__meta">
                            {employee.registrationNumber ? `Matrícula ${employee.registrationNumber} · ` : ""}
                            CPF {formatCpf(employee.cpf)}
                          </span>
                        </div>
                        <button
                          type="button"
                          className="dpBtn dpBtn--secondary dpBtn--small"
                          onClick={() => handleOpenEmployee(employee.id)}
                        >
                          Abrir ficha
                        </button>
                      </div>
                      <ul className="dpPendingItem__list">
                        {employee.pendencies.map((pendency) => (
                          <li key={pendency.id} className="dpPendingItem__row">
                            <span className={`dpBadge ${CATEGORY_BADGE[pendency.category] ?? "dpBadge--gray"}`}>
                              {PENDENCY_CATEGORY_LABELS[pendency.category] ?? pendency.category}
                            </span>
                            <span className="dpPendingItem__text">{pendency.description}</span>
                            <button
                              type="button"
                              className="dpBtn dpBtn--primary dpBtn--small"
                              disabled={resolving === pendency.id}
                              onClick={() => setConfirming({ employeeName: employee.fullName, pendency })}
                            >
                              {resolving === pendency.id ? "..." : "Resolver"}
                            </button>
                          </li>
                        ))}
                      </ul>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <footer className="dpModal__footer">
              <div className="dpPagination" style={{ width: "100%" }}>
                <span>
                  {total} colaborador{total === 1 ? "" : "es"} com pendência
                </span>
                <div className="dpPagination__btns">
                  <button
                    type="button"
                    className="dpBtn dpBtn--secondary dpBtn--small"
                    disabled={page <= 1 || loading}
                    onClick={() => setPage((p) => p - 1)}
                  >
                    Anterior
                  </button>
                  <span>
                    Página {page} de {totalPages}
                  </span>
                  <button
                    type="button"
                    className="dpBtn dpBtn--secondary dpBtn--small"
                    disabled={page >= totalPages || loading}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Próxima
                  </button>
                </div>
              </div>
            </footer>
          </div>
        </div>
      )}

      {/* Depois do modal no DOM: mesmo z-index, fica por cima dele. */}
      <EcoConfirmDialog
        open={!!confirming}
        title="Marcar pendência como resolvida?"
        message={
          confirming ? (
            <>
              <p>
                <strong>{confirming.employeeName}</strong> ·{" "}
                {PENDENCY_CATEGORY_LABELS[confirming.pendency.category] ?? confirming.pendency.category}
              </p>
              <p>{confirming.pendency.description}</p>
              <p>A pendência sai da lista. Confirme só se o cadastro já foi corrigido.</p>
            </>
          ) : null
        }
        confirmLabel="Resolver"
        busy={!!resolving}
        onConfirm={handleResolve}
        onCancel={() => setConfirming(null)}
      />
    </>
  );
};

export default PendingRegistrationsNotice;
